import { roundToPrecision } from "../../../helpers/math";
import { centsToDollarsDisplay } from "../../displayFormatters";
import { StatisticItem } from "../ReportStatistics";
import { ManHoursProjectData, ManHoursReportData } from "./manHoursReportApi";

export const getUniqueSalesPersonNames = (manHoursProjects: ManHoursProjectData[]): string[] => {
  const salesPersonNames = new Set<string>();

  manHoursProjects.forEach((project: ManHoursProjectData) => {
    salesPersonNames.add(project.salesPersonDisplayName);
  });


  return Array.from(salesPersonNames);
};

export const getUniqueDivisionNames = (manHoursProjects: ManHoursProjectData[]): string[] => {
  const divisionNames = new Set<string>();

  manHoursProjects.forEach((project: ManHoursProjectData) => {
    divisionNames.add(project.divisionName);
  });

  return Array.from(divisionNames);
};

export const getEstimatedManHoursStatistics = (manHours: ManHoursReportData): StatisticItem[] => {  
  return [
    {
      title: 'Man Hours',
      value: typeof manHours.estimatedManHours === 'number' && !isNaN(manHours.estimatedManHours) ? roundToPrecision(manHours.estimatedManHours, 1) : '-',
    },
    {
      title: 'Revenue per Hour',
      value: typeof manHours.estimatedRevenuePerManHour === 'number' && !isNaN(manHours.estimatedRevenuePerManHour) ? (centsToDollarsDisplay(manHours.estimatedRevenuePerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Gross Profit per Hour',
      value: typeof manHours.estimatedGrossProfitPerManHour === 'number' && !isNaN(manHours.estimatedGrossProfitPerManHour) ? (centsToDollarsDisplay(manHours.estimatedGrossProfitPerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Net Profit per Hour',
      value: typeof manHours.estimatedNetProfitPerManHour === 'number' && !isNaN(manHours.estimatedNetProfitPerManHour) ? (centsToDollarsDisplay(manHours.estimatedNetProfitPerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Throughput per Hour',
      value: typeof manHours.estimatedThroughputPerManHour === 'number' && !isNaN(manHours.estimatedThroughputPerManHour) ? (centsToDollarsDisplay(manHours.estimatedThroughputPerManHour) || '-') : '-',
      tag: 'insight',
      tooltip: 'Throughput per Man-hour tells you how much revenue you produce internally in your own company. Revenue you receive to cover pass-through vendor (ie subcontractors, materials, etc) expenses is not counted.',
    },
    {
      title: 'Direct Labor Efficiency Ratio',
      value: typeof manHours.estimatedDirectLaborEfficiencyRatio === 'number' && !isNaN(manHours.estimatedDirectLaborEfficiencyRatio) ? `${roundToPrecision(manHours.estimatedDirectLaborEfficiencyRatio, 2)}` : '-',
      tag: 'insight',
      tooltip: 'The ratio of per dollar spent on direct labor to gross profit. The higher the ratio, the more efficient your direct labor is.ie a ratio of 2.5 means that for every dollar you spend on labor, you produce $2.50 in gross profit'
    },
    {
      title: 'Direct Labor Ratio',
      value: typeof manHours.estimatedDirectLaborRevenueRatio === 'number' && !isNaN(manHours.estimatedDirectLaborRevenueRatio) ? `${roundToPrecision(manHours.estimatedDirectLaborRevenueRatio, 1)}%` : '-',
      tag: 'insight',
      tooltip: 'Direct Labor Ratio shows you what percentage of your revenue went to direct labor costs. The lower the percentage, the more revenue per direct employee your company can produce.',
    },
  ]
}

export const getActualManHoursStatistics = (manHours: ManHoursReportData): StatisticItem[] => {  
  return [
    {
      title: 'Man Hours',
      value: typeof manHours.actualManHours === 'number' && !isNaN(manHours.actualManHours) ? roundToPrecision(manHours.actualManHours, 1) : '-',
    },
    {
      title: 'Revenue per Hour',
      value: typeof manHours.actualRevenuePerManHour === 'number' && !isNaN(manHours.actualRevenuePerManHour) ? (centsToDollarsDisplay(manHours.actualRevenuePerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Gross Profit per Hour',
      value: typeof manHours.actualGrossProfitPerManHour === 'number' && !isNaN(manHours.actualGrossProfitPerManHour) ? (centsToDollarsDisplay(manHours.actualGrossProfitPerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Net Profit per Hour',
      value: typeof manHours.actualNetProfitPerManHour === 'number' && !isNaN(manHours.actualNetProfitPerManHour) ? (centsToDollarsDisplay(manHours.actualNetProfitPerManHour) || '-') : '-',
      tag: 'insight',
    },
    {
      title: 'Throughput per Hour',
      value: typeof manHours.actualThroughputPerManHour === 'number' && !isNaN(manHours.actualThroughputPerManHour) ? (centsToDollarsDisplay(manHours.actualThroughputPerManHour) || '-') : '-',
      tag: 'insight',
      tooltip: 'Throughput per Man-hour tells you how much revenue you produce internally in your own company. Revenue you receive to cover pass-through vendor (ie subcontractors, materials, etc) expenses is not counted.',
    },
    {
      title: 'Direct Labor Efficiency Ratio',
      value: typeof manHours.actualDirectLaborEfficiencyRatio === 'number' && !isNaN(manHours.actualDirectLaborEfficiencyRatio) ? `${roundToPrecision(manHours.actualDirectLaborEfficiencyRatio, 2)}` : '-',
      tag: 'insight',
      tooltip: 'The ratio of per dollar spent on direct labor to gross profit. The higher the ratio, the more efficient your direct labor is.ie a ratio of 2.5 means that for every dollar you spend on labor, you produce $2.50 in gross profit'
    },
    {
      title: 'Direct Labor Ratio',
      value: typeof manHours.actualDirectLaborRevenueRatio === 'number' && !isNaN(manHours.actualDirectLaborRevenueRatio) ? `${roundToPrecision(manHours.actualDirectLaborRevenueRatio, 1)}%` : '-',
      tag: 'insight',
      tooltip: 'Direct Labor Ratio shows you what percentage of your revenue went to direct labor costs. The lower the percentage, the more revenue per direct employee your company can produce.',
    },
  ]
}

export const getJobTypes = (items: ManHoursReportData): number[] => {
  const jobTypes = new Set<number>();
  items.projects.forEach(item => {
    jobTypes.add(item.jobType);
  });

  return jobTypes.size ? Array.from(jobTypes) : [];
}

export const defaultManHoursReport: ManHoursReportData = {
  projects: [],
  actualDirectLaborEfficiencyRatio: 0,
  actualDirectLaborRevenueRatio: 0,
  actualNetProfitPerManHour: 0,
  actualRevenuePerManHour: 0,
  actualThroughputPerManHour: 0,
  estimatedDirectLaborEfficiencyRatio: 0,
  estimatedDirectLaborRevenueRatio: 0,
  estimatedNetProfitPerManHour: 0,
  estimatedRevenuePerManHour: 0,
  estimatedThroughputPerManHour: 0,
  estimatedGrossProfitPerManHour: 0,
  actualGrossProfitPerManHour: 0,
  estimatedManHours: 0,
  actualManHours: 0,
}

export const getFilteredManHoursReport = (projects: ManHoursProjectData[], jobTypeSet: Set<number>, salesPersonIds: string[], divisionIds: string[]): ManHoursReportData => {

  if(projects.length === 0) {
    return defaultManHoursReport
  }

  const filteredProjects = projects.filter(x =>
    (salesPersonIds.length === 0 || salesPersonIds.includes(x.salesPersonDisplayName)) &&
    (jobTypeSet.size === 0 || jobTypeSet.has(x.jobType)) &&
    (divisionIds.length === 0 || divisionIds.includes(x.divisionName))
  )

  const estimatedRevenue = filteredProjects.reduce((acc, curr) => acc + curr.estimatedPriceFinal, 0)
  const estimatedManHours = filteredProjects.reduce((acc, curr) => acc + curr.estimatedLaborQty, 0)
  const estimatedEquipmentCost = filteredProjects.reduce((acc, curr) => acc + curr.estimatedEquipmentCost, 0)
  const estimatedMaterialsCost = filteredProjects.reduce((acc, curr) => acc + curr.estimatedMaterialsCost, 0)
  const estimatedLaborCost = filteredProjects.reduce((acc, curr) => acc + curr.estimatedLaborCost, 0)
  const estimatedSubcontractingCost = filteredProjects.reduce((acc, curr) => acc + curr.estimatedSubcontractingCost, 0)
  const estimatedOverhead = filteredProjects.reduce((acc, curr) => acc + curr.estimatedOverhead, 0)

  const estimatedThroughput = estimatedRevenue - (estimatedEquipmentCost + estimatedMaterialsCost + estimatedSubcontractingCost)
  const estimatedGrossProfit = estimatedRevenue - (estimatedEquipmentCost + estimatedMaterialsCost + estimatedSubcontractingCost + estimatedLaborCost)
  const estimatedNetProfit = estimatedRevenue - (estimatedEquipmentCost + estimatedMaterialsCost + estimatedSubcontractingCost + estimatedLaborCost + estimatedOverhead)

  const actualRevenue = filteredProjects.reduce((acc, curr) => acc + curr.actualPriceFinal, 0)
  const actualManHours = filteredProjects.reduce((acc, curr) => acc + curr.actualLaborQty, 0)
  const actualEquipmentCost = filteredProjects.reduce((acc, curr) => acc + curr.actualEquipmentCost, 0)
  const actualMaterialsCost = filteredProjects.reduce((acc, curr) => acc + curr.actualMaterialsCost, 0)
  const actualLaborCost = filteredProjects.reduce((acc, curr) => acc + curr.actualLaborCost, 0)
  const actualSubcontractingCost = filteredProjects.reduce((acc, curr) => acc + curr.actualSubcontractingCost, 0)
  const actualOverhead = filteredProjects.reduce((acc, curr) => acc + curr.actualOverhead, 0)

  const actualThroughput = actualRevenue - (actualEquipmentCost + actualMaterialsCost + actualSubcontractingCost)
  const actualGrossProfit = actualRevenue - (actualEquipmentCost + actualMaterialsCost + actualSubcontractingCost + actualLaborCost)
  const actualNetProfit = actualRevenue - (actualEquipmentCost + actualMaterialsCost + actualSubcontractingCost + actualLaborCost + actualOverhead)

  return {
    projects: filteredProjects.map(project => ({
      ...project,
      projectId: project.projectId,
      jobName: project.jobName,
      jobType: project.jobType,
      salesPersonName: project.salesPersonDisplayName,
      isSold: project.actualPriceFinal > 0,
      displayName: project.customerDisplayName,
      propertyId: project.propertyId,
      address1: project.address1,
      city: project.city,
      state: project.state,
      projectWorkareaId: project.projectWorkareaId,
      estimatedManHours: roundToPrecision(project.estimatedLaborQty, 1),
      actualManHours: roundToPrecision(project.actualLaborQty, 1),
    })),
    estimatedDirectLaborEfficiencyRatio: roundToPrecision(estimatedGrossProfit / estimatedLaborCost, 1),
    estimatedDirectLaborRevenueRatio: roundToPrecision((estimatedLaborCost / estimatedRevenue) * 100, 1),
    estimatedNetProfitPerManHour: roundToPrecision(estimatedNetProfit / estimatedManHours, 1),
    estimatedRevenuePerManHour: roundToPrecision(estimatedRevenue / estimatedManHours, 1),
    estimatedThroughputPerManHour: roundToPrecision(estimatedThroughput / estimatedManHours, 1),
    estimatedGrossProfitPerManHour: roundToPrecision(estimatedGrossProfit / estimatedManHours, 1),
    estimatedManHours: roundToPrecision(estimatedManHours, 1),
    actualGrossProfitPerManHour: roundToPrecision(actualGrossProfit / actualManHours, 1),
    actualDirectLaborEfficiencyRatio: roundToPrecision(actualGrossProfit / actualLaborCost, 1),
    actualDirectLaborRevenueRatio: roundToPrecision((actualLaborCost / actualRevenue) * 100, 1),
    actualNetProfitPerManHour: roundToPrecision(actualNetProfit / actualManHours, 1),
    actualRevenuePerManHour: roundToPrecision(actualRevenue / actualManHours, 1),
    actualThroughputPerManHour: roundToPrecision(actualThroughput / actualManHours, 1),
    actualManHours: roundToPrecision(actualManHours, 1)
  }
}